import React, { useCallback, useMemo } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import {
  MinusCircleIcon,
  SparklesIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { ImportLineItem, LineClassification, ClassificationStatus } from '../types';
import {
  getAvailableLots,
  getAvailableEnsembles,
  getAvailableSousEnsembles,
  getLotCategoryLabel,
} from '../data/classifyingData';
import SearchableDropdown from './SearchableDropdown';

const STATUS_CONFIG: Record<
  ClassificationStatus,
  { icon: React.ElementType; color: string; title: string }
> = {
  unclassified: { icon: MinusCircleIcon, color: 'text-gray-300', title: 'Non classé' },
  suggested: { icon: SparklesIcon, color: 'text-amber-400', title: 'Suggestion' },
  changed: { icon: PencilSquareIcon, color: 'text-violet-400', title: 'Modifié' },
  validated: { icon: CheckCircleIcon, color: 'text-emerald-500', title: 'Validé' },
};

interface ClassifyingCellsProps {
  line: ImportLineItem;
  selectedIds: Set<string>;
  onUpdateClassification: (id: string, classification: LineClassification) => void;
  onBatchClassify: (ids: string[], partial: Partial<LineClassification>) => void;
}

const ClassifyingCells: React.FC<ClassifyingCellsProps> = ({
  line,
  selectedIds,
  onUpdateClassification,
  onBatchClassify,
}) => {
  const lotId = line.classification?.lotId ?? null;
  const ensembleId = line.classification?.ensembleId ?? null;
  const sousEnsembleId = line.classification?.sousEnsembleId ?? null;
  const status = line.classification?.status ?? 'unclassified';

  const lots = useMemo(() => getAvailableLots(), []);
  const ensembles = useMemo(() => getAvailableEnsembles(lotId), [lotId]);
  const sousEnsembles = useMemo(() => getAvailableSousEnsembles(ensembleId), [ensembleId]);

  const isSelected = selectedIds.has(line.id);

  // If this line is selected and there are other selected lines, apply to all
  const applyToSelection = useCallback(
    (partial: Partial<LineClassification>) => {
      if (isSelected && selectedIds.size > 1) {
        onBatchClassify(Array.from(selectedIds), partial);
      } else {
        onUpdateClassification(line.id, {
          lotId,
          ensembleId,
          sousEnsembleId,
          status: 'unclassified',
          ...partial,
        });
      }
    },
    [isSelected, selectedIds, onBatchClassify, onUpdateClassification, line.id, lotId, ensembleId, sousEnsembleId]
  );

  const handleLotChange = useCallback(
    (newLotId: string | null) => {
      applyToSelection({ lotId: newLotId, ensembleId: null, sousEnsembleId: null });
    },
    [applyToSelection]
  );

  const handleEnsembleChange = useCallback(
    (newEnsembleId: string | null) => {
      applyToSelection({ ensembleId: newEnsembleId, sousEnsembleId: null });
    },
    [applyToSelection]
  );

  const handleSousEnsembleChange = useCallback(
    (newSousEnsembleId: string | null) => {
      applyToSelection({ sousEnsembleId: newSousEnsembleId });
    },
    [applyToSelection]
  );

  // Title rows → empty cells to keep alignment
  if (line.isTitle) {
    return (
      <>
        <td className="pl-3 pr-1" />
        <td className="w-6" />
      </>
    );
  }

  const { icon: StatusIcon, color, title } = STATUS_CONFIG[status];

  return (
    <>
      <td className="pl-3 pr-1" onClick={(e) => e.stopPropagation()}>
        <div className="flex gap-1">
          <div className="flex-1 min-w-0">
            <SearchableDropdown
              options={lots}
              value={lotId}
              onChange={handleLotChange}
              getLabel={(l) => l.nom}
              getId={(l) => l.id}
              getGroup={(l) => getLotCategoryLabel(l.id)}
              placeholder="Lot…"
              size="sm"
            />
          </div>
          <div className="flex-1 min-w-0">
            <SearchableDropdown
              options={ensembles}
              value={ensembleId}
              onChange={handleEnsembleChange}
              getLabel={(e) => e.nom}
              getId={(e) => e.id}
              placeholder="Ensemble…"
              disabled={!lotId}
              disabledHint="Sélectionnez un lot d'abord"
              size="sm"
            />
          </div>
          <div className="flex-1 min-w-0">
            <SearchableDropdown
              options={sousEnsembles}
              value={sousEnsembleId}
              onChange={handleSousEnsembleChange}
              getLabel={(se) => se.nom}
              getId={(se) => se.id}
              placeholder="Sous-ens…"
              disabled={!ensembleId}
              disabledHint="Sélectionnez un ensemble d'abord"
              size="sm"
            />
          </div>
        </div>
      </td>
      <td className="w-6 text-center" title={title}>
        <StatusIcon className={`w-4 h-4 inline-block ${color}`} />
      </td>
    </>
  );
};

export default ClassifyingCells;
